import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import Seo from "../components/Seo";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_carmarket-preview/artifacts/vmguiuiy_logo%20v9%20use-02%282%29.png";

export default function Register() {
  const { register } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", password: "", role: "user" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    const res = await register(form);
    setLoading(false);
    if (res.ok) {
      toast.success(t("auth.created_toast"));
      navigate(form.role === "dealer" ? "/dealer-dashboard" : "/account");
    } else {
      setError(res.error || "Registration failed");
    }
  };

  const update = (k, v) => setForm({ ...form, [k]: v });

  return (
    <div data-testid="register-page" className="min-h-[80vh] bg-slate-50 flex items-center justify-center px-4 py-10">
      <Seo title={t("auth.signup")} description={t("auth.signup_sub")} />
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-7 fade-in-up">
        <div className="flex items-center gap-2.5 mb-6">
          <div className="w-11 h-11 rounded-xl border border-slate-200 overflow-hidden">
            <img src={LOGO_URL} alt={t("brand.name")} className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="font-display font-extrabold text-base text-cm-primary">{t("brand.name")}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-cm-accent">{t("brand.tagline")}</div>
          </div>
        </div>
        <h1 className="font-display font-extrabold text-2xl text-cm-primary">{t("auth.signup_title")}</h1>
        <p className="text-sm text-slate-500 mt-1">{t("auth.signup_sub")}</p>

        <div className="mt-5 grid grid-cols-2 gap-2">
          {[{ id: "user", label: t("auth.role_buyer") }, { id: "dealer", label: t("auth.role_dealer") }].map((r) => (
            <button key={r.id} type="button" data-testid={`role-${r.id}`} onClick={() => update("role", r.id)}
              className={`px-3 py-2.5 rounded-xl text-sm font-medium border-2 transition-all ${form.role === r.id ? 'border-cm-accent bg-red-50 text-cm-primary' : 'border-slate-200 text-slate-600'}`}>
              {r.label}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="mt-4 space-y-3">
          <Field testid="reg-name" label={t("auth.full_name")} value={form.full_name} onChange={(v) => update("full_name", v)} required />
          <Field testid="reg-email" label={t("auth.email")} type="email" value={form.email} onChange={(v) => update("email", v)} required />
          <Field testid="reg-phone" label={t("auth.phone_optional")} value={form.phone} onChange={(v) => update("phone", v)} />
          <Field testid="reg-password" label={t("auth.password")} type="password" value={form.password} onChange={(v) => update("password", v)} required />
          {error && <div data-testid="register-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>}
          <button data-testid="register-submit" disabled={loading} type="submit" className="w-full px-5 py-3 rounded-xl bg-cm-primary text-white font-medium disabled:opacity-60 flex items-center justify-center gap-2 press-scale hover:bg-cm-accent">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} {t("auth.signup")}
          </button>
        </form>

        <p className="mt-5 text-sm text-slate-500 text-center">
          {t("auth.have_account")} <Link data-testid="register-to-login" to="/login" className="text-cm-accent font-medium hover:underline">{t("auth.signin")}</Link>
        </p>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5 font-semibold">{label}</span>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:bg-white focus:border-cm-accent outline-none" />
    </label>
  );
}
